import { Component, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { MeterAbstractService } from '../../../services/meter.abstract.service';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { UserResponse } from 'src/app/user/user.model';
import { LinkMeterBody } from '../../../models/meter-manage.model';

@Component({
    selector: 'app-link-meter-dialog',
    templateUrl: './link-meter-dialog.component.html',
    styleUrls: ['./link-meter-dialog.component.scss'],
})
export class LinkMeterDialogComponent implements OnDestroy {
    private destroy$ = new Subject<void>();
    private userSearch$ = new Subject<string>();

    nodeId = '';
    nodeType = 'WATER_METER';
    street = '';
    city = '';
    postCode = '';
    unit = '';

    userSearchTerm = '';
    userResults: UserResponse[] = [];
    selectedUser: UserResponse | null = null;
    searchingUsers = false;

    submitting = false;
    error = '';

    nodeTypeOptions = ['WATER_METER'];

    constructor(
        public dialogRef: MatDialogRef<LinkMeterDialogComponent>,
        private meterService: MeterAbstractService,
        private userService: UserService,
    ) {
        this.userSearch$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    if (!term || term.length < 2) {
                        return of({ docs: [] });
                    }
                    this.searchingUsers = true;
                    return this.userService.quickSearch(term, 1, 10).pipe(catchError(() => of({ docs: [] })));
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((result: any) => {
                this.userResults = result.docs || [];
                this.searchingUsers = false;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onUserSearch(value: string): void {
        this.userSearchTerm = value;
        this.selectedUser = null;
        this.userSearch$.next(value);
    }

    selectUser(user: UserResponse): void {
        this.selectedUser = user;
        this.userSearchTerm = `${user.firstName} ${user.lastName}`;
        this.userResults = [];
    }

    get isValid(): boolean {
        return !!(this.nodeId.trim() && this.nodeType && this.street.trim() && this.city.trim() && this.postCode.trim() && this.selectedUser);
    }

    onSubmit(): void {
        if (!this.isValid || this.submitting) return;

        this.submitting = true;
        this.error = '';

        const body: LinkMeterBody = {
            nodeId: this.nodeId.trim(),
            nodeType: this.nodeType,
            address: {
                street: this.street.trim(),
                city: this.city.trim(),
                postCode: this.postCode.trim(),
            },
        };

        if (this.unit.trim()) {
            body.address.unit = this.unit.trim();
        }

        this.meterService
            .linkMeter(this.selectedUser._id, body)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.submitting = false;
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    this.submitting = false;
                    if (err.status === 409) {
                        this.error = 'meter.manage.link_error_conflict';
                    } else {
                        this.error = 'meter.manage.link_error_generic';
                    }
                },
            });
    }

    close(): void {
        this.dialogRef.close(false);
    }
}
